import type { Quote } from '../api/types.js';

/**
 * Reine Darstellung eines Belegs: Kopfdaten, Positionen, Summen.
 *
 * Herausgeloest aus `QuotePanel`, damit Kundenworkflow und Admin-Archiv dieselbe
 * Ansicht zeigen und es keine zweite Angebotsdarstellung gibt. Diese Komponente
 * kennt **keine Aktion**: kein Ausstellen, kein Verwerfen, kein PDF-Knopf -- das
 * entscheidet der Aufrufer je nach Kontext.
 *
 * Die Preise stammen ausnahmslos vom Server. Hier wird nichts nachgerechnet -- eine
 * Zahl im Frontend waere eine zweite Preiswahrheit.
 */
export function QuoteView({
  quote,
  mitBezeichnung = false,
}: {
  quote: Quote;
  /**
   * Zeigt zusaetzlich die Artikelbezeichnung je Position. Im Kundenworkflow aus,
   * weil der Anwender die Artikel gerade selbst ausgewaehlt hat; im Archiv an, weil
   * dort die Artikelnummer allein wenig sagt.
   */
  mitBezeichnung?: boolean;
}): React.JSX.Element {
  return (
    <>
      <dl className="kopfdaten">
        {quote.recipientName !== null && (
          <>
            <dt>Firma</dt>
            <dd>
              {quote.recipientName}
              {quote.recipientNumber !== null && ` (${quote.recipientNumber})`}
            </dd>
          </>
        )}
        <dt>Status</dt>
        <dd>{statusText(quote.status)}</dd>
        <dt>Angebotsdatum</dt>
        <dd>{quote.quoteDate ?? 'wird beim Ausstellen gesetzt'}</dd>
        {quote.supersedesQuote !== null && (
          <>
            <dt>Ersetzt</dt>
            <dd>{quote.supersedesQuote.quoteNumber}</dd>
          </>
        )}
      </dl>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Pos.</th>
              <th scope="col">Artikel</th>
              {mitBezeichnung && <th scope="col">Bezeichnung</th>}
              <th scope="col">Menge</th>
              <th scope="col">Listenpreis</th>
              <th scope="col">Rabatt</th>
              <th scope="col">Netto/Stk.</th>
              <th scope="col">Positionswert</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((i) => (
              <tr
                key={i.position}
                // G-ST-P/OE: Profil und Ring derselben Dichtung sichtbar verbunden,
                // wie im PDF -- keine Trennlinie zwischen den beiden Zeilen.
                style={i.oeRole === 'PROFILE' ? { borderBottom: 'none' } : undefined}
              >
                <td>{i.position}</td>
                <th scope="row">
                  <code>{i.articleNumber}</code>
                  {i.oeRole !== null && !mitBezeichnung && (
                    <div className="note">{i.description}</div>
                  )}
                </th>
                {mitBezeichnung && <td>{i.description}</td>}
                <td className="num">{menge(i.quantity)}</td>
                <td className="num">{i.listPrice} €</td>
                <td className="num">{i.discountPercent} %</td>
                <td className="num">{i.netUnitPrice} €</td>
                <td className="num">{i.lineNet} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="totals">
        <dt>Warenwert</dt>
        <dd>{quote.goodsValue} €</dd>
        {quote.minOrderSurcharge !== null && Number(quote.minOrderSurcharge) > 0 && (
          <>
            <dt>
              Mindermengenzuschlag
              <span className="note"> (unter {quote.appliedMinOrderThreshold} €)</span>
            </dt>
            <dd>{quote.minOrderSurcharge} €</dd>
          </>
        )}
        <dt>Verpackung und Fracht Inland</dt>
        <dd>
          {quote.packagingFreightAmount === null ? (
            <span className="muted">nicht hinterlegt</span>
          ) : (
            `${quote.packagingFreightAmount} €`
          )}
        </dd>
        <dt className="total">Gesamt netto</dt>
        <dd className="total">
          {quote.documentTotalNet === null ? (
            <span className="muted">offen</span>
          ) : (
            `${quote.documentTotalNet} €`
          )}
        </dd>
      </dl>
    </>
  );
}

/** Statuswerte des Belegs in Anwendersprache. Unbekanntes bleibt, wie es ist. */
export function statusText(status: Quote['status'] | string): string {
  switch (status) {
    case 'draft':
      return 'Entwurf';
    case 'issued':
      return 'ausgestellt';
    case 'expired':
      return 'abgelaufen';
    case 'withdrawn':
      return 'zurueckgezogen';
    default:
      return status;
  }
}

/** Menge ohne bedeutungslose Nachkommastellen. */
export function menge(wert: string): string {
  return wert.includes('.') ? wert.replace(/\.?0+$/, '') : wert;
}
